import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { TextInput } from '@hologram-dimension/text-input';
import { type FC, useCallback, useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useInstantSearch, useSearchBox } from 'react-instantsearch';
import clsx from 'clsx';
import styles from './SearchBox.module.css';

interface SearchBoxFormProps {
  search: string;
}

const SearchBox: FC = () => {
  const { status } = useInstantSearch();
  const { query, refine } = useSearchBox();

  const isSearchStalled = status === 'stalled';

  const { register, handleSubmit, watch, reset } = useForm<SearchBoxFormProps>({
    defaultValues: { search: query },
    mode: 'onChange',
  });

  const commitStateChange = useCallback<SubmitHandler<SearchBoxFormProps>>(
    (data) => {
      refine(data.search);
    },
    [refine]
  );

  useEffect(() => {
    const subscription = watch(() => handleSubmit(commitStateChange)());
    return () => subscription.unsubscribe();
  }, [commitStateChange, handleSubmit, watch]);

  const onReset = useCallback(() => {
    reset({ search: '' });
  }, [reset]);

  useEffect(() => {
    if (!query || query.length === 0) {
      reset({ search: '' });
    }
  }, [query, reset]);

  return (
    <form onSubmit={handleSubmit(commitStateChange)} className={styles.wrapper}>
      <TextInput
        placeholder="Search by country or network name..."
        id="query"
        autocomplete="off"
        autocorrect="off"
        autocapitalize="none"
        spellCheck="false"
        iconStart={<Icon name="Search" size="medium" />}
        type="search"
        className={styles.textInput}
        {...register('search')}
      />

      <Button
        aria-label="Reset"
        type="button"
        onClick={onReset}
        className={clsx(styles.resetButton, (!query || isSearchStalled) && styles.hidden)}
        iconStart={<Icon name="Close" size="medium" />}
      />
    </form>
  );
};
export default SearchBox;
